type SignsRows = Array<Array<{ length: number }>>;

/**
 * Builds a map of sign counts to their frequencies across rows.
 *
 * @param rows The rows to analyze
 * @returns A map where keys are sign counts and values are the frequency of rows with that count
 */
export function buildSignsMap(rows: SignsRows): Map<number, number> {
  const signsMap = new Map<number, number>();

  for (const row of rows) {
    const rowCount = row.reduce((count, outcomeGroup) => count + outcomeGroup.length, 0);
    signsMap.set(rowCount, (signsMap.get(rowCount) ?? 0) + 1);
  }

  return signsMap;
}

/* Checks if target system size can be reached exactly by only adding signs to existing rows. */
function isTargetSizeReachable({
  targetSize,
  signsMap,
  outcomeCount,
}: {
  targetSize: number;
  signsMap: Map<number, number>;
  outcomeCount: number;
}): boolean {
  const signCounts: number[] = [];

  for (const [rawSignCount, frequency] of signsMap.entries()) {
    const signCount = Math.min(Math.max(rawSignCount, 1), outcomeCount);
    for (let i = 0; i < frequency; i++) {
      signCounts.push(signCount);
    }
  }

  if (signCounts.length === 0) {
    return true;
  }

  let reachableProducts = new Set<number>([1]);

  for (const currentSignCount of signCounts) {
    const nextReachableProducts = new Set<number>();

    for (const product of reachableProducts) {
      const maxReachableSigns = Math.min(outcomeCount, Math.floor(targetSize / product));
      for (let signs = currentSignCount; signs <= maxReachableSigns; signs++) {
        nextReachableProducts.add(product * signs);
      }
    }

    reachableProducts = nextReachableProducts;
  }

  return reachableProducts.has(targetSize);
}

/**
 * Determines if a given size is disabled for the specified signs configuration.
 * A size is disabled if it's not greater than the system size or cannot be reached
 * by adding signs to existing rows.
 *
 * @param root0 Function arguments
 * @param root0.size The size to check for being disabled
 * @param root0.systemSize The current system size of the outcomes
 * @param root0.signsMap A map of sign counts to their frequencies across rows
 * @param root0.outcomeCount The total number of outcomes available for signs
 * @returns True if the size is disabled, false otherwise
 */
export function isSizeDisabledForSigns({
  size,
  systemSize,
  signsMap,
  outcomeCount,
}: {
  size: number;
  systemSize: number;
  signsMap: Map<number, number>;
  outcomeCount: number;
}): boolean {
  if (size <= systemSize) {
    return true;
  }

  return !isTargetSizeReachable({
    targetSize: size,
    signsMap,
    outcomeCount,
  });
}
